/**
 * App Configuration
 */

// Cache for tenant config
let cachedTenantConfig: TenantAppConfig | null = null;
let cacheExpiry: number = 0;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Runtime-resolved tenant ID (set once during init)
let _runtimeTenantId: string | null = null;

export interface TenantAppConfig {
  tenantId: string;
  liffId: string;
  apiBaseUrl: string;
  supportedLanguages: string[];
  defaultLanguage: string;
  features: Record<string, boolean>;
}

/**
 * Get API Base URL - dynamically resolves based on current hostname
 */
export function getApiBaseUrl(): string {
  // Check environment variable first
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // Server-side rendering fallback
  if (typeof window === 'undefined' || !window.location) {
    return 'http://localhost:4001';
  }

  const { protocol, hostname } = window.location;

  // Map frontend hostnames to API hostname (webhook-server)
  const mapping: Record<string, string> = {
    'mini-app.metagoldtech.com': 'webhook-server.metagoldtech.com',
    'lineoa-hotel-prod.web.app': 'webhook-server.metagoldtech.com',
    'hotel-admin.metagoldtech.com': 'webhook-server.metagoldtech.com',
    'hotel-app.metagoldtech.com': 'webhook-server.metagoldtech.com',
    'hotel.metagoldtech.com': 'webhook-server.metagoldtech.com',
  };

  const apiHost =
    mapping[hostname] ||
    (hostname === 'localhost' ? 'localhost:4001' : 'webhook-server.metagoldtech.com');
  const url = hostname === 'localhost' ? `http://${apiHost}` : `${protocol}//${apiHost}`;

  console.log('[MiniApp Config] Resolved API_BASE:', url, '(from host:', hostname, ')');
  return url;
}

/**
 * Resolve tenant ID at runtime.
 * Priority: 1) URL ?tenant= param  2) VITE_TENANT_ID env  3) 'demo'
 */
export function getTenantId(): string {
  if (_runtimeTenantId) return _runtimeTenantId;
  return import.meta.env.VITE_TENANT_ID || '';
}

export function setRuntimeTenantId(tenantId: string): void {
  _runtimeTenantId = tenantId;
  try { sessionStorage.setItem('_tenantId', tenantId); } catch (_) { /* ignore */ }
}

export async function resolveTenantByLiff(liffId: string): Promise<string | null> {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/resolve-tenant/${liffId}`);
    if (!response.ok) return null;
    const result = await response.json();
    return result.success ? result.data.tenantId : null;
  } catch (error) {
    console.error('[MiniApp Config] Error resolving tenant by LIFF:', error);
    return null;
  }
}

/**
 * Detect and lock tenant ID from URL query param.
 * Persists to sessionStorage so the value survives LIFF login redirects.
 * Must be called once during app init, before any API calls.
 */
export function initTenantFromUrl(): string {
  if (typeof window !== 'undefined' && window.location) {
    const params = new URLSearchParams(window.location.search);
    const tenantParam = params.get('tenant');
    if (tenantParam && /^[a-zA-Z0-9_-]{1,64}$/.test(tenantParam)) {
      _runtimeTenantId = tenantParam;
      try { sessionStorage.setItem('_tenantId', tenantParam); } catch (_) { /* ignore */ }
      console.log('[MiniApp Config] Tenant from URL param:', tenantParam);
    }
  }
  // Recover from sessionStorage (survives LIFF login redirect that strips query params)
  if (!_runtimeTenantId) {
    try {
      const stored = sessionStorage.getItem('_tenantId');
      if (stored && /^[a-zA-Z0-9_-]{1,64}$/.test(stored)) {
        _runtimeTenantId = stored;
        console.log('[MiniApp Config] Tenant from sessionStorage:', stored);
      }
    } catch (_) { /* ignore */ }
  }
  // Fall back to env / default (NO 'demo' fallback)
  if (!_runtimeTenantId) {
    _runtimeTenantId = import.meta.env.VITE_TENANT_ID || '';
  }
  return _runtimeTenantId;
}

let _runtimeHotelCode: string | null = null;

export function setRuntimeHotelCode(hotelCode: string): void {
  if (hotelCode && typeof hotelCode === 'string') {
    _runtimeHotelCode = hotelCode;
  }
}

export function getHotelCode(): string {
  if (_runtimeHotelCode) return _runtimeHotelCode;
  return import.meta.env.VITE_HOTEL_CODE || '';
}

/**
 * Fetch tenant config from API (with caching)
 */
export async function fetchTenantConfig(): Promise<TenantAppConfig | null> {
  // Check cache first
  if (cachedTenantConfig && Date.now() < cacheExpiry) {
    return cachedTenantConfig;
  }

  try {
    const baseUrl = getApiBaseUrl();
    const tenantId = getTenantId();
    const response = await fetch(`${baseUrl}/api/${tenantId}/config`);

    if (!response.ok) {
      console.error('[MiniApp Config] Failed to fetch tenant config:', response.status);
      return null;
    }

    const data = await response.json();
    if (data.success) {
      cachedTenantConfig = data.data;
      cacheExpiry = Date.now() + CACHE_TTL_MS;
      console.log('[MiniApp Config] Loaded tenant config:', cachedTenantConfig);
      return cachedTenantConfig;
    }

    return null;
  } catch (error) {
    console.error('[MiniApp Config] Error fetching tenant config:', error);
    return null;
  }
}

/**
 * Get LIFF ID - from API (runtime) or env var (build-time fallback)
 */
export async function getLiffId(): Promise<string> {
  // Try runtime API first (SaaS mode — different tenant may have different LIFF ID)
  const tenantConfig = await fetchTenantConfig();
  if (tenantConfig?.liffId) {
    return tenantConfig.liffId;
  }

  // Fallback to build-time env (single-tenant dev mode)
  return import.meta.env.VITE_LIFF_ID || '';
}

export const config = {
  // LIFF ID resolved async — use getLiffId() instead for runtime value
  liffId: import.meta.env.VITE_LIFF_ID || '',
  get apiBaseUrl() {
    return getApiBaseUrl();
  },
  get tenantId() {
    return getTenantId();
  },
};

// Supported languages
export const LANGUAGES = ['th', 'en', 'zh'] as const;
export type Language = (typeof LANGUAGES)[number];

// Booking constants
export const MAX_ADULTS = 10;
export const MAX_CHILDREN = 10;
export const MAX_ROOMS = 10;
export const MIN_NIGHTS = 1;
export const MAX_NIGHTS = 30;
